import React, { useEffect, useState } from "react";
import { IconButton, Tooltip, HStack } from "@chakra-ui/react";
import { GoCheck } from "react-icons/go";
import { MdBlock } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";

import EditableTable from "../../../components/editableTable/EditableTable";
import { getAccountsInfo, updateAccountInfo, upgradeAccount } from "../../../slice/adminHomeSlice";
import { AccountTypeEnum, UpgradeRequestEnum } from "../../../utils/enums";
import { useQueryState, parseAsInteger } from "nuqs";

function UpgradeAccountRequestTable() {
  const dispatch = useDispatch();
  const accountsInfoOriginal = useSelector((state) => state.adminHome.accountsInfo);

  const accountsInfo = accountsInfoOriginal.map((x) => ({ ...x }));
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));
  const [pageSize, setPageSize] = useQueryState("pageSize", parseAsInteger.withDefault(10));

  /* ---------------- Search ---------------- */

  const searchText = "Search by username";

  const searchFn = (e) => {
    if (searchTimeout) clearTimeout(searchTimeout);

    setSearchTimeout(
      setTimeout(() => {
        dispatch(getAccountsInfo(e.target.value));
      }, 500),
    );
  };

  /* ---------------- Action Buttons ---------------- */

  const ActionButton = ({ onClick, icon, tooltip, colorScheme }) => (
    <Tooltip label={tooltip} hasArrow>
      <IconButton size="sm" icon={icon} onClick={onClick} aria-label={tooltip} variant="outline" colorScheme={colorScheme} />
    </Tooltip>
  );

  /* ---------------- Build Manage Column ---------------- */

  accountsInfo.forEach((info) => {
    const acceptOnClick = async () => {
      await dispatch(
        upgradeAccount({
          username: info.username,
          action: UpgradeRequestEnum.APPROVED,
          accountType: info.requestAccountType,
          entityName: info.entityName,
          sector: info.sector || null,
          sectorName: info.sectorName || null,
        }),
      );
    };

    const denyOnClick = async () => {
      await dispatch(
        upgradeAccount({
          username: info.username,
          action: UpgradeRequestEnum.DENIED,
        }),
      );
    };

    info.manage = (
      <HStack justify="center" spacing={3}>
        <ActionButton onClick={acceptOnClick} icon={<GoCheck />} tooltip="Accept upgrade request" colorScheme="green" />
        <ActionButton onClick={denyOnClick} icon={<MdBlock />} tooltip="Deny upgrade request" colorScheme="red" />
      </HStack>
    );
  });

  /* ---------------- Initial Load ---------------- */

  useEffect(() => {
    dispatch(getAccountsInfo());
  }, [dispatch]);

  /* ---------------- EditableTable Config ---------------- */

  const submitHandler = async (x) => (await dispatch(updateAccountInfo(x))).payload;

  const columnOptions = [
    {
      key: "username",
      editable: () => false,
      columnName: "User Name",
    },
    {
      key: "accountType",
      editable: () => false,
      type: "select",
      values: Object.values(AccountTypeEnum),
      filter: true,
      filterValue: AccountTypeEnum.NORMAL_USER,
      columnName: "Account Type",
    },
    {
      key: "upgradeRequest",
      editable: () => false,
      type: "select",
      values: Object.values(UpgradeRequestEnum),
      filter: true,
      filterValue: UpgradeRequestEnum.PROCESSING,
      columnName: "Status",
    },
    {
      key: "requestAccountType",
      editable: () => false,
      columnName: "Requested Account Type",
    },
    {
      key: "entityName",
      editable: () => false,
      columnName: "Entity Name",
    },
    {
      key: "sector",
      editable: () => false,
      columnName: "Sector",
      render: (row) => row.sectorName || row.sector || "-",
    },
    {
      key: "manage",
      editable: () => false,
      columnName: "Actions",
      columnWidth: "180px",
    },
  ];

  /* ---------------- Render ---------------- */

  return <EditableTable tableName="Account Upgrade Requests" tableData={accountsInfo} columnOptions={columnOptions} submitHandler={submitHandler} searchFn={searchFn} searchText={searchText} page={page} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} />;
}

export default UpgradeAccountRequestTable;
